import { FC, useMemo } from 'react';
import styled from 'styled-components';
import { Colors, Spacing, FontSize } from '../../../assets/GlobalStyle/Variables';
import { PAGINATION_PER_PAGE_LIMIT } from '../../../helpers/constants';
import { useSidebarContext } from '../Sidebar/SidebarContext';

type PaginationProps = {
  count: number;
  page: number;
  offset: number;
  limit?: number;
  onSubmit: (offset: number, page: number) => void;
  onLimitChange?: (limit: number) => void;
  showInfo?: boolean;
  showLimitSelector?: boolean;
};

const PAGE_NEIGHBORS = 1;
const LIMIT_OPTIONS = [10, 20, 50, 100];

export const Pagination: FC<PaginationProps> = (props) => {
  const {
    onSubmit,
    count,
    page,
    offset,
    limit = PAGINATION_PER_PAGE_LIMIT,
    onLimitChange,
    showInfo = true,
    showLimitSelector = true
  } = props;

  const { isCollapsed } = useSidebarContext();

  const countPages = Math.ceil(count / limit);
  const startRecord = count === 0 ? 0 : offset + 1;
  const endRecord = Math.min(offset + limit, count);

  const pageNumbers = useMemo(() => {
    if (countPages <= 7) {
      return Array.from({ length: countPages }, (_, i) => i + 1);
    }

    const pages: (number | string)[] = [];
    const leftBound = Math.max(2, page - PAGE_NEIGHBORS);
    const rightBound = Math.min(countPages - 1, page + PAGE_NEIGHBORS);

    pages.push(1);

    if (leftBound > 2) {
      pages.push('ellipsis-left');
    }

    for (let i = leftBound; i <= rightBound; i++) {
      pages.push(i);
    }

    if (rightBound < countPages - 1) {
      pages.push('ellipsis-right');
    }

    if (countPages > 1) {
      pages.push(countPages);
    }

    return pages;
  }, [page, countPages]);

  const handlePageChange = (newPage: number) => {
    if (newPage < 1 || newPage > countPages || newPage === page) return;
    const newOffset = (newPage - 1) * limit;
    onSubmit(newOffset, newPage);
  };

  const handleLimitChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newLimit = Number(e.target.value);
    if (onLimitChange) {
      onLimitChange(newLimit);
    }
  };

  if (count === 0) {
    return null;
  }

  return (
    <PaginationContainer $isCollapsed={isCollapsed}>
      {showInfo && (
        <PaginationInfo>
          Zobrazeno {startRecord}-{endRecord} ze {count} záznamů
        </PaginationInfo>
      )}

      <PaginationControls>
        <NavigationButton
          onClick={() => handlePageChange(1)}
          disabled={page === 1}
          aria-label="První stránka"
          title="První stránka">
          &#171;
        </NavigationButton>

        <NavigationButton
          onClick={() => handlePageChange(page - 1)}
          disabled={page === 1}
          aria-label="Předchozí stránka"
          title="Předchozí stránka">
          &#8249;
        </NavigationButton>

        {pageNumbers.map((pageNum, index) => {
          if (typeof pageNum === 'string') {
            return (
              <Ellipsis key={pageNum} aria-hidden="true">
                ...
              </Ellipsis>
            );
          }

          return (
            <PageButton
              key={pageNum}
              onClick={() => handlePageChange(pageNum)}
              $active={pageNum === page}
              disabled={pageNum === page}
              aria-label={`Stránka ${pageNum}`}
              aria-current={pageNum === page ? 'page' : undefined}>
              {pageNum}
            </PageButton>
          );
        })}

        <NavigationButton
          onClick={() => handlePageChange(page + 1)}
          disabled={page === countPages}
          aria-label="Další stránka"
          title="Další stránka">
          &#8250;
        </NavigationButton>

        <NavigationButton
          onClick={() => handlePageChange(countPages)}
          disabled={page === countPages}
          aria-label="Poslední stránka"
          title="Poslední stránka">
          &#187;
        </NavigationButton>
      </PaginationControls>

      {showLimitSelector && onLimitChange && (
        <LimitSelector>
          <LimitLabel htmlFor="pagination-limit">Položek na stránku:</LimitLabel>
          <LimitSelect id="pagination-limit" value={limit} onChange={handleLimitChange}>
            {LIMIT_OPTIONS.map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </LimitSelect>
        </LimitSelector>
      )}
    </PaginationContainer>
  );
};

const PaginationContainer = styled.div<{ $isCollapsed: boolean }>`
  display: flex;
  flex-direction: column;
  gap: ${Spacing(4)};
  width: auto;
  align-items: center;
  position: fixed;
  bottom: 0;
  left: ${(props) => (props.$isCollapsed ? '59px' : '178px')};
  right: 0;
  background-color: #ffffffec;
  padding: ${Spacing(4)};
  border-top: 2px solid ${Colors.lightGrey};
  box-shadow: 0 -2px 10px rgba(0, 0, 0, 0.1);
  z-index: 1;
  transition: left 0.3s ease;

  @media (min-width: 768px) {
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
  }
`;

const PaginationInfo = styled.div`
  color: ${Colors.grey};
  font-size: 14px;
  white-space: nowrap;
  margin-left: ${Spacing(4)};
`;

const PaginationControls = styled.div`
  display: flex;
  gap: ${Spacing(1)};
  align-items: center;
  flex-wrap: wrap;
  justify-content: center;
`;

const BaseButton = styled.button`
  background: ${Colors.white};
  border: 2px solid ${Colors.lightGrey};
  border-radius: 6px;
  color: ${Colors.grey};
  cursor: pointer;
  font-size: ${FontSize.normal};
  font-weight: 500;
  min-width: 40px;
  height: 40px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  user-select: none;

  &:hover:not(:disabled) {
    background: ${Colors.primary};
    border-color: ${Colors.primary};
    color: ${Colors.white};
    transform: translateY(-1px);
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  }

  &:active:not(:disabled) {
    transform: translateY(0);
    box-shadow: none;
  }

  &:disabled {
    background-color: ${Colors.disabled};
    border-color: ${Colors.lightGrey};
    color: ${Colors.disabledHover};
    cursor: not-allowed;
    opacity: 0.6;
  }
`;

const NavigationButton = styled(BaseButton)`
  font-size: 20px;
  font-weight: 700;
  padding: 0 ${Spacing(2)};
`;

const PageButton = styled(BaseButton)<{ $active: boolean }>`
  ${(props) =>
    props.$active &&
    `
    background: ${Colors.primary};
    border-color: ${Colors.primary};
    color: ${Colors.white};
    font-weight: 700;
    cursor: default;
    pointer-events: none;
  `}
`;

const Ellipsis = styled.span`
  color: ${Colors.grey};
  padding: 0 ${Spacing(1)};
  user-select: none;
  display: inline-flex;
  align-items: center;
  height: 40px;
`;

const LimitSelector = styled.div`
  display: flex;
  align-items: center;
  gap: ${Spacing(2)};
  white-space: nowrap;
`;

const LimitLabel = styled.label`
  color: ${Colors.grey};
  font-size: 14px;
  display: flex;
  align-items: center;
  height: 40px;
  margin-bottom: 0;
`;

const LimitSelect = styled.select`
  background: ${Colors.white};
  border: 2px solid ${Colors.lightGrey};
  border-radius: 6px;
  color: ${Colors.grey};
  cursor: pointer;
  font-size: ${FontSize.normal};
  padding: ${Spacing(2)} ${Spacing(3)};
  height: 40px;
  transition: all 0.2s ease;

  &:hover {
    border-color: ${Colors.primary};
  }

  &:focus {
    outline: none;
    border-color: ${Colors.primary};
    box-shadow: 0 0 0 3px rgba(60, 17, 73, 0.1);
  }
`;
